import ufc from "@tvg/api/ufc";
import { Dispatch } from "redux";
import { get } from "lodash";
import {
  setFavoriteTrack,
  removeFavoriteTrack,
  setUserFavoriteTracksWithId
} from "@tvg/shared-actions/UserActions";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import mediator from "@tvg/mediator";
import * as mediatorClassic from "@tvg/mediator-classic/src";
import { NullaryFn } from "@tvg/ts-types/Functional";
import { setUserFavoriteTracks } from "../store/actions";
import { favoriteInteractionEvent } from "./gtm/favorites";
import { FavoriteWithIdTracks, Favorite } from "../types";

export const fetchUserFavoriteTracks = async (
  accountNumber: string,
  dispatch: Dispatch
) => {
  const favoriteTracksWithId: FavoriteWithIdTracks = {};
  const favoriteTracks: string[] = [];
  try {
    if (accountNumber) {
      const { data } = await ufc.getUserAlerts(accountNumber, "track");
      const ufcFavoriteTracks = await data.favorites;
      ufcFavoriteTracks.forEach((favorite: Favorite) => {
        favoriteTracksWithId[favorite.entityName] = favorite.favoriteId;
        favoriteTracks.push(favorite.entityName);
      });
      dispatch(setUserFavoriteTracksWithId(favoriteTracksWithId));
      dispatch(setUserFavoriteTracks(favoriteTracks));
    }
  } catch (e) {
    console.error("Error while fetching");
  }
};

export const updateFavorites = ({
  dispatch,
  isAddingFavorite,
  trackCode,
  setIsFavorite,
  isLogged,
  accountNumber,
  favoriteId,
  isAccountCompliant,
  onFinish
}: {
  dispatch: Dispatch;
  isAddingFavorite: boolean;
  trackCode: string;
  setIsFavorite: (value: SetFavoriteState) => void;
  isLogged: boolean;
  accountNumber: string;
  favoriteId: number | null;
  isAccountCompliant?: boolean;
  onFinish?: NullaryFn<void>;
}) => {
  const addOrRemoveFavorites = () => {
    if (isAddingFavorite) {
      addTrackToFavorites(
        dispatch,
        trackCode,
        setIsFavorite,
        accountNumber
      ).then(() => onFinish?.());
    } else {
      removeTrackFromFavorites(
        dispatch,
        trackCode,
        accountNumber,
        setIsFavorite,
        favoriteId
      ).then(() => onFinish?.());
    }
  };

  const openLoginWall = () => {
    mediator.base.dispatch({
      type: "TVG_LOGIN:OPEN_LOGIN_MODAL",
      payload: {
        callback: (error: Error, success: Object) => {
          if (!error && success) {
            const localAccountNumber = get(
              success,
              "data.userDetails.accountNumber",
              null
            );

            if (typeof localAccountNumber === "string") {
              addTrackToFavorites(
                dispatch,
                trackCode,
                setIsFavorite,
                localAccountNumber
              ).then(() => onFinish?.());
            }
          }
        },
        triggerAction: "tracks_favorite_change"
      }
    });
  };

  if (isLogged) {
    if (isAccountCompliant) {
      addOrRemoveFavorites();
    } else {
      onOpenComplianceModal();
      onFinish?.();
    }
  } else {
    openLoginWall();
    onFinish?.();
  }
};

type SetFavoriteState = (prevState: boolean) => boolean;

export const addTrackToFavorites = async (
  dispatch: Dispatch,
  trackCode: string,
  setIsFavorite: (value: SetFavoriteState) => void,
  accountNumber: string
) => {
  try {
    const { data } = await ufc.addFavorite(accountNumber, "track", [trackCode]);
    const { favoriteId } = data[0];
    dispatch(setFavoriteTrack(trackCode, favoriteId));
    setIsFavorite((prevState) => !prevState);
    updateSessionFavoriteTracks(true, trackCode, favoriteId);
    favoriteInteractionEvent({ isFavorite: true });
  } catch (error) {
    console.error("Error while adding favorite");
  }
};

export const removeTrackFromFavorites = async (
  dispatch: Dispatch,
  trackCode: string,
  accountNumber: string,
  setIsFavorite: (value: SetFavoriteState) => void,
  favoriteId: number | null
) => {
  try {
    if (!favoriteId) return;
    await ufc.removeFavorite(accountNumber, favoriteId);
    dispatch(removeFavoriteTrack(trackCode));
    setIsFavorite((prevState) => !prevState);
    updateSessionFavoriteTracks(false, trackCode, favoriteId);
    favoriteInteractionEvent({ isFavorite: false });
  } catch (error) {
    console.error("Error while removing favorite");
  }
};

export const updateSessionFavoriteTracks = (
  isAddingFavorite: boolean,
  trackCode: string,
  favoriteId: number | null
) => {
  const favorites = (sessionStorage.getItem("favoriteTracks") || "").split(",");
  if (isAddingFavorite) {
    if (!favorites.includes(trackCode)) {
      favorites.push(trackCode);
    }
  } else {
    favorites.splice(favorites.indexOf(trackCode), 1);
  }
  mediator.base.dispatch({
    type: "UPDATE_SESSION_FAVORITE_TRACKS",
    payload: {
      isAddingFavorite,
      trackCode,
      favoriteId
    }
  });
  mediatorClassic.dispatch("UPDATE_FAVORITE_TRACKS", {
    favorites,
    trackCode,
    favoriteId
  });
};

export const fetchUserFavoriteRunners = async (accountNumber: string) => {
  const favoriteRunners: string[] = [];
  try {
    if (accountNumber) {
      const { data } = await ufc.getUserAlerts(accountNumber, "runner");
      const ufcFavoriteRunners = data.favorites;
      ufcFavoriteRunners.forEach((favorite: Favorite) => {
        favoriteRunners.push(favorite.entityName);
      });
    }
  } catch (e) {
    console.error("Error while fetching", e);
  }
  return favoriteRunners;
};
